"use client";

import {
  RefObject,
  useEffect,
  useRef,
  useState,
  VideoHTMLAttributes,
} from "react";
import styles from "./HeroVideo.module.css";
import { useMatchMedia, useMatchMediaListener } from "hooks/useMatchMedia";
import classNames from "classnames";

function useVideo(): VideoHTMLAttributes<HTMLVideoElement> & {
  ref: RefObject<HTMLVideoElement>;
} {
  const prefersReducedMotion = useMatchMedia(
    "(prefers-reduced-motion: reduce)"
  );

  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    async function setPlayPauseState() {
      try {
        if (prefersReducedMotion) {
          ref.current?.pause();
        } else {
          await ref.current?.play();
        }
      } catch (e) {
        // Play can throw in certain situations, like the phone wants to save
        // battery, a user is navigating away or the browser doesn't allow
        // playing. Since it doesn't matter if the banner plays or not, we can
        // ignore the error
        console.error(e);
      }
    }

    setPlayPauseState();
  }, [prefersReducedMotion]);

  return {
    autoPlay: !prefersReducedMotion,
    ref,
    loop: true,
    muted: true,
    playsInline: true,
    preload: "none",
  };
}

const sources = {
  mobile: [
    { src: "/videos/hero-mobile.webm", mime: "video/webm" },
    { src: "/videos/hero-mobile.mp4", mime: "video/mp4" },
  ],
  desktop: [
    { src: "/videos/hero-desktop.webm", mime: "video/webm" },
    { src: "/videos/hero-desktop.mp4", mime: "video/mp4" },
  ],
};

const HeroVideo = () => {
  const props = useVideo();
  const isMobile = useMatchMediaListener("(max-width: 768px)");

  const [videoSources, setVideoSources] = useState<
    { src: string; mime: string }[]
  >(isMobile ? sources["desktop"] : sources["mobile"]);

  useEffect(() => {
    if (isMobile) {
      setVideoSources(sources["mobile"]);
    } else {
      setVideoSources(sources["desktop"]);
    }
  }, [isMobile]);

  return (
    <div className={classNames(styles.container)} key={videoSources[0].src}>
      <video {...props} className={styles.video}>
        {videoSources.map(({ src, mime }) => (
          <source key={src} src={src} type={mime} />
        ))}
      </video>
    </div>
  );
};

export default HeroVideo;
